import { memo } from "react";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Edit2,
  Lock,
  Users,
  Copy,
  Trash2,
  CheckCircle,
  XCircle,
  Key,
  Plus,
  Settings,
  Search,
  Filter,
} from "lucide-react";
import { Tooltip } from "@mui/material";
import { Role } from "../../../types/Role";

// Helper to count permissions from the structure
const countPermissions = (permissions: any): number => {
  try {
    if (!permissions || typeof permissions !== "object") return 0;

    // Handle "all": ["*"] case
    if (
      permissions.all &&
      Array.isArray(permissions.all) &&
      permissions.all.includes("*")
    ) {
      return Infinity;
    }

    // Count all actions across all modules
    return Object.values(permissions).reduce((total: number, actions: any) => {
      if (Array.isArray(actions)) {
        return total + actions.length;
      }
      return total;
    }, 0);
  } catch (error) {
    console.error("Error counting permissions:", error);
    return 0;
  }
};

// Helper to format permissions count
const formatPermissionsCount = (permissions: any): string => {
  const count = countPermissions(permissions);
  if (count === Infinity || count > 100) {
    return "All";
  }
  return count.toString();
};

// ==================== ROLES HEADER ====================
export const RolesHeader = memo(
  ({ rolesCount, onSeedDefaults, onCreateNew, canAddEdit }: any) => {
    const { t } = useTranslation();

    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-gray-600 to-black p-3 rounded-xl shadow-lg">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
              {t("Roles")}
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              {t("Manage roles and permissions")} • {rolesCount}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* <button
            onClick={onSeedDefaults}
            className="flex-1 sm:flex-none px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-semibold text-sm flex items-center justify-center gap-2 active:scale-95"
          >
            <Settings size={16} />
            <span className="hidden sm:inline">{t("Seed Defaults")}</span>
            <span className="sm:hidden">{t("Seed")}</span>
          </button> */}
          <Tooltip
            title={
              canAddEdit
                ? t("Add New Role")
                : t("You don't have permission to create new role")
            }
            placement="top"
          >
            <button
              disabled={!canAddEdit}
              onClick={onCreateNew}
              className="flex-1 border border-gray-600 sm:flex-none px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-gray-700 hover:text-white transition-all font-semibold text-sm flex items-center justify-center gap-2 active:scale-95"
            >
              <Plus size={16} />
              {t("New Role")}
            </button>
          </Tooltip>
        </div>
      </div>
    );
  },
);

// ==================== ROLES FILTERS ====================
export const RolesFilters = memo(
  ({
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    viewMode,
    setViewMode,
  }: any) => {
    const { t } = useTranslation();

    return (
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder={t("Search roles...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none text-sm bg-white"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-indigo-500 focus:outline-none appearance-none text-sm bg-white"
            >
              <option value="all">{t("All Status")}</option>
              <option value="active">{t("Active")}</option>
              <option value="inactive">{t("Inactive")}</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("cards")}
              className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all active:scale-95 ${
                viewMode === "cards"
                  ? "bg-gray-800 text-white shadow-md"
                  : "bg-white text-gray-800 border-2 border-gray-200 hover:border-gray-800"
              }`}
            >
              {t("Cards")}
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`flex-1 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all active:scale-95 ${
                viewMode === "table"
                  ? "bg-gray-800 text-white shadow-md"
                  : "bg-white text-gray-800 border-2 border-gray-200 hover:border-gray-800"
              }`}
            >
              {t("Table")}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

// ==================== ROLE CARD ====================
export const RoleCard = memo(
  ({
    role,
    canEdit,
    canDelete,
    onEdit,
    onPermissions,
    onUsers,
    onDuplicate,
    onDelete,
    onToggleStatus,
  }: any) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";

    const formatDate = (dateString: string) => {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return "Invalid Date";
      }
    };

    const displayName = role.displayName
      ? isArabic
        ? role.displayName.ar
        : role.displayName.en
      : role.name;

    const description =
      typeof role.description === "string"
        ? role.description
        : (isArabic ? role.description?.ar : role.description?.en) ||
          t("No description");

    return (
      <div className="bg-gray-100 border border-1 border-gray-300 rounded-xl p-4 sm:p-6 transform transition-all duration-300 ease-out shadow-md -translate-y-0.5 hover:-translate-y-1 hover:scale-105 hover:shadow-xl hover:border-indigo-200">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-base sm:text-lg truncate capitalize">
                {displayName}
              </h3>
              {role.isSystem && (
                <span className="inline-block text-xs px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full font-semibold mt-1">
                  {t("System")}
                </span>
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[40px]">
          {description}
        </p>

        <div className="space-y-3 mb-4 bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 flex items-center gap-1 font-medium">
              <Key size={14} />
              {t("Permissions")}
            </span>
            <span className="text-indigo-600 font-semibold">
              {formatPermissionsCount(role.permissions)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-600">
            <span className="text-gray-600 flex items-center gap-1 font-medium">
              <Users size={14} />
              {t("Users")}
            </span>
            <span className="font-semibold text-indigo-600">
              {role.userCount || 0}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => onToggleStatus(role)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold transition-all active:scale-95 ${
              role.isActive
                ? "bg-green-100 text-green-800 hover:bg-green-200"
                : "bg-red-100 text-red-800 hover:bg-red-200"
            }`}
          >
            {role.isActive ? (
              <>
                <CheckCircle size={14} />
                {t("Active")}
              </>
            ) : (
              <>
                <XCircle size={14} />
                {t("Inactive")}
              </>
            )}
          </button>
          <span className="text-xs text-gray-500">
            {formatDate(role.createdAt)}
          </span>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          <Tooltip title={t("Edit")} placement="top">
            <button
              disabled={!canEdit}
              onClick={() => onEdit(role)}
              className="px-2 sm:px-3 py-2 border border-1 border-indigo-600 text-indigo-600 bg-white rounded-lg hover:bg-indigo-600 hover:text-white transition-all text-sm font-semibold flex items-center justify-center active:scale-95"
            >
              <Edit2 size={14} />
            </button>
          </Tooltip>
          <Tooltip title={t("Permissions")} placement="top">
            <button
              disabled={!canEdit}
              onClick={() => onPermissions(role)}
              className="px-2 sm:px-3 py-2 border border-1 border-blue-600 text-blue-600 bg-white rounded-lg hover:bg-blue-600 hover:text-white transition-all text-sm font-semibold flex items-center justify-center active:scale-95"
            >
              <Lock size={14} />
            </button>
          </Tooltip>
          <Tooltip title={t("Users")} placement="top">
            <button
              onClick={() => onUsers(role)}
              className="px-2 sm:px-3 py-2 border border-1 border-green-600 text-green-600 bg-white rounded-lg hover:bg-green-600 hover:text-white transition-all text-sm font-semibold flex items-center justify-center active:scale-95"
            >
              <Users size={14} />
            </button>
          </Tooltip>
          <Tooltip title={t("Duplicate")} placement="top">
            <button
              onClick={() => onDuplicate(role)}
              className="px-2 sm:px-3 py-2 border border-1 border-purple-600 text-purple-600 bg-white rounded-lg hover:bg-purple-600 hover:text-white transition-all text-sm font-semibold flex items-center justify-center active:scale-95"
            >
              <Copy size={14} />
            </button>
          </Tooltip>
          {!role.isSystem && (
            <Tooltip title={t("Delete")} placement="top">
              <button
                disabled={!canDelete}
                onClick={() => onDelete(role)}
                className="px-2 sm:px-3 py-2 border border-1 border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-600 hover:text-white transition-all text-sm font-semibold flex items-center justify-center active:scale-95"
              >
                <Trash2 size={14} />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    );
  },
);

// ==================== ROLES GRID ====================
export const RolesGrid = memo(
  ({
    roles,
    canEdit,
    canDelete,
    onEdit,
    onPermissions,
    onUsers,
    onDuplicate,
    onDelete,
    onToggleStatus,
  }: any) => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {roles.map((role: Role) => (
          <RoleCard
            key={role.id}
            role={role}
            canEdit={canEdit}
            canDelete={canDelete}
            onEdit={onEdit}
            onPermissions={onPermissions}
            onUsers={onUsers}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
          />
        ))}
      </div>
    );
  },
);

// ==================== ROLES TABLE ====================
export const RolesTable = memo(
  ({
    roles,
    canEdit,
    canDelete,
    onEdit,
    onPermissions,
    onUsers,
    onDuplicate,
    onDelete,
    onToggleStatus,
  }: any) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";

    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Role")}
              </th>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Description")}
              </th>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Permissions")}
              </th>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Users")}
              </th>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Status")}
              </th>
              <th className="px-4 sm:px-6 py-4 text-left text-sm font-bold text-gray-700">
                {t("Actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {roles.map((role: Role) => {
              const displayName = role.displayName
                ? isArabic
                  ? role.displayName.ar
                  : role.displayName.en
                : role.name;

              const description =
                typeof role.description === "string"
                  ? role.description
                  : (isArabic ? role.description?.ar : role.description?.en) ||
                    t("No description");

              return (
                <tr
                  key={role.id}
                  className="bg-white hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      <div className="font-semibold text-gray-900 truncate capitalize">
                        {displayName}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="text-sm text-gray-600 max-w-xs truncate">
                      {description}
                    </div>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold">
                      <Lock size={14} />
                      {formatPermissionsCount(role.permissions)}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                      <Users size={14} />
                      {role.userCount || 0}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <button
                      onClick={() => onToggleStatus(role)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                        role.isActive
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-red-100 text-red-800 hover:bg-red-200"
                      }`}
                    >
                      {role.isActive ? (
                        <>
                          <CheckCircle size={14} />
                          {t("Active")}
                        </>
                      ) : (
                        <>
                          <XCircle size={14} />
                          {t("Inactive")}
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 sm:px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Tooltip title={t("Edit")} placement="top">
                        <button
                          disabled={!canEdit}
                          onClick={() => onEdit(role)}
                          className="p-2 border-1 border border-indigo-600 text-indigo-600 bg-white rounded-lg hover:bg-indigo-600 hover:text-white transition-all active:scale-95"
                        >
                          <Edit2 size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip title={t("Permissions")} placement="top">
                        <button
                          disabled={!canEdit}
                          onClick={() => onPermissions(role)}
                          className="p-2 border border-1 border-blue-400 text-blue-600 bg-white rounded-lg hover:bg-blue-600 hover:text-white transition-all active:scale-95"
                        >
                          <Lock size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip title={t("Users")} placement="top">
                        <button
                          onClick={() => onUsers(role)}
                          className="p-2 border border-1 border-green-600 text-green-600 bg-white rounded-lg hover:bg-green-600 hover:text-white transition-all active:scale-95"
                        >
                          <Users size={16} />
                        </button>
                      </Tooltip>
                      {!role.isSystem && (
                        <Tooltip title={t("Delete")} placement="top">
                          <button
                            disabled={!canDelete}
                            onClick={() => onDelete(role)}
                            className="p-2 border border-1 border-red-600 text-red-600 bg-white rounded-lg hover:bg-red-600 hover:text-white transition-all active:scale-95"
                          >
                            <Trash2 size={16} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  },
);
